import { useSnackbarStore } from "@/store/zustandStore";
import { ExtendedUser, NotificationType, User } from "@/types";
import { PopupCard, Toggle } from "@app-components";
import { useEffect, useId, useState } from "react";
import { getUserByEmail } from "@/api";
import { PersonSearch } from "./PersonSearch";
import { useTranslation } from "react-i18next";
import { Tooltip } from "react-tooltip";
import styles from "@/styles/AddAdminModal.module.scss";

type AddUserToRoleModalProps = {
  header: string;
  body: string;
  noResultsText: string;
  existingAdmins: ExtendedUser[] | User[];
  searchCompanyId?: string;
  authCompanyId?: string;
  disallowedUsers?: string[];
  disallowedMessage?: string;
  onClose: () => void;
  onAdd: (user: User) => Promise<void>;
  successMessageKey: string;
  alreadyExistsMessageKey: string;
  errorMessageKey: string;
  open: boolean;
  showAccessAdminToggle?: boolean;
  canManageAccessAdmin?: boolean;
  onAssignAccessAdmin?: (user: User) => Promise<void>;
  accessAdminLabelKey?: string;
  accessAdminDescriptionKey?: string;
  accessAdminDisabledTooltipKey?: string;
  accessAdminAssignFailedWarningKey?: string;
};

export default function AddUserToRoleModal({
  header,
  body,
  noResultsText,
  existingAdmins,
  searchCompanyId,
  authCompanyId,
  disallowedUsers = [],
  disallowedMessage,
  onClose,
  onAdd,
  successMessageKey,
  alreadyExistsMessageKey,
  errorMessageKey,
  open,
  showAccessAdminToggle = false,
  canManageAccessAdmin = true,
  onAssignAccessAdmin,
  accessAdminLabelKey,
  accessAdminDescriptionKey,
  accessAdminDisabledTooltipKey,
  accessAdminAssignFailedWarningKey,
}: AddUserToRoleModalProps) {
  const { t } = useTranslation();
  const { addNotification } = useSnackbarStore();
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [accessAdmin, setAccessAdmin] = useState(true);
  const [selectedUser, setSelectedUser] = useState<User | undefined>(undefined);
  const accessAdminToggleTooltipId = useId();
  const shouldRenderAccessAdminToggle = showAccessAdminToggle && !!selectedUser;
  const isAccessAdminToggleChecked = canManageAccessAdmin ? accessAdmin : false;
  const shouldAssignAccessAdmin = canManageAccessAdmin && accessAdmin;

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setSelectedUser(undefined);
      setAccessAdmin(true);
      setIsAdding(false);
      setIsSearching(false);
      return;
    }

    // Always start from a clean state when the modal is opened.
    setSelectedUser(undefined);
  }, [open]);

  const searchForUser = async (text: string) => {
    if (!text) {
      return [];
    }
    setIsSearching(true);
    try {
      return searchCompanyId
        ? await getUserByEmail(text, searchCompanyId, true, authCompanyId)
        : await getUserByEmail(text, undefined, undefined, authCompanyId);
    } catch (error) {
      console.error(error);
      return [];
    } finally {
      setIsSearching(false);
    }
  };

  const addRoleToUser = async (user: User) => {
    if (isAdding) {
      return;
    }

    if (existingAdmins.some((admin) => admin.userId === user?.userId)) {
      setIsAdding(false);
      addNotification(
        t(alreadyExistsMessageKey, {
          userName: user.firstName,
        }),
        NotificationType.SUCCESS,
      );
      onClose();
      return;
    }

    setIsAdding(true);
    try {
      await onAdd(user);
      addNotification(
        t(successMessageKey, {
          userName: user.firstName,
        }),
        NotificationType.SUCCESS,
      );

      if (shouldAssignAccessAdmin && onAssignAccessAdmin) {
        void onAssignAccessAdmin(user).catch((error) => {
          console.error(error);
          addNotification(
            t(accessAdminAssignFailedWarningKey ?? "UPDATE_FAILED"),
            NotificationType.WARNING,
          );
        });
      }
      onClose();
    } catch (error) {
      console.error(error);
      addNotification(
        t(errorMessageKey, {
          userName: user.firstName,
        }),
      );
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <PopupCard onClose={onClose} open={open}>
      <PersonSearch
        dark
        showAddButton
        extraPaddingBottom={!shouldRenderAccessAdminToggle}
        header={header}
        body={body}
        noResultsText={noResultsText}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        isSearching={isSearching}
        searchForUser={searchForUser}
        isAddButtonDisabled={isAdding}
        addUser={addRoleToUser}
        isLoading={isAdding}
        showCompany
        disallowedMessage={disallowedMessage}
        disallowedUsers={disallowedUsers}
        onSelectedUserChange={setSelectedUser}
      />
      {shouldRenderAccessAdminToggle && (
        <div className={styles.accessAdminRow}>
          <span className={styles.accessAdminLabelSection}>
            <span className={styles.accessAdminLabel}>
              {t(accessAdminLabelKey ?? "COMPANY_ADMIN.COLUMNS.ACCESS_ADMIN")}
            </span>
            <span className={styles.accessAdminDescription}>
              {t(
                accessAdminDescriptionKey ?? "ACCESS_ADMIN.INVITE.DESCRIPTION",
              )}
            </span>
          </span>
          <span
            data-tooltip-id={accessAdminToggleTooltipId}
            data-tooltip-content={
              !canManageAccessAdmin && accessAdminDisabledTooltipKey
                ? t(accessAdminDisabledTooltipKey)
                : undefined
            }
          >
            <Toggle
              dark
              stopPropagation
              hideLabel
              label={t(
                accessAdminLabelKey ?? "COMPANY_ADMIN.COLUMNS.ACCESS_ADMIN",
              )}
              checked={isAccessAdminToggleChecked}
              disabled={!canManageAccessAdmin}
              onChange={setAccessAdmin}
            />
          </span>
          {!canManageAccessAdmin && accessAdminDisabledTooltipKey && (
            <Tooltip id={accessAdminToggleTooltipId} />
          )}
        </div>
      )}
      {shouldRenderAccessAdminToggle && <div className={styles.toggleSpacer} />}
    </PopupCard>
  );
}
